<template>
    <DCard padding>
        <DCol>
            <DRow
                class="mb-3"
                align-vertical="end"
            >
                <DCol
                    sm="12"
                >
                    <DButton
                        id="table-configuration"
                        icon="cog-outline"
                        :label="__('messages.list_configuration')"
                        class="float-end"
                        @click="showListViewModal = true"
                    />
                </DCol>
            </DRow>
            <DRow
                class="mb-3"
                align-vertical="end"
            >
                <DCol sm="1">
                    <DDateFormatInput
                        id="date-from"
                        v-model="filterParams.from_date"
                        format="YYYY-MM-DD"
                        type="date"
                        :have-class="'mb-0'"
                        :label="__('tools.invoices.filters.from_date')"
                        add-input-classes="bg-light"
                        class="mb-0"
                        @keydown.enter.exact.prevent="changePage(1)"
                    />
                </DCol>
                <DCol sm="1">
                    <DDateFormatInput
                        id="date-to"
                        v-model="filterParams.to_date"
                        format="YYYY-MM-DD"
                        type="date"
                        :have-class="'mb-0'"
                        :label="__('tools.invoices.filters.to_date')"
                        add-input-classes="bg-light"
                        class="mb-0"
                        @keydown.enter.exact.prevent="changePage(1)"
                    />
                </DCol>
                <DCol sm="2">
                    <DSelect
                        id="country"
                        v-model="filterParams.country_code"
                        :label="__('tools.invoices.filters.country')"
                        :options="countryOptions"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        :label="__('customers.contracts.filters.type')"
                        :options="typeOptions"
                        :selection="true"
                        selection-type="text"
                        @update="updateSelectedTypes"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        :label="__('customers.contracts.filters.status')"
                        :options="statusOptions"
                        :selection="true"
                        selection-type="text"
                        @update="updateSelectedStatuses"
                    />
                </DCol>
                <DCol sm="2">
                    <DSelect
                        id="divergent-diamant-status"
                        v-model="filterParams.divergent_diamant_status"
                        :label="__('tools.invoices.filters.divergent_diamant_status')"
                        :options="divergentDiamantStatuses"
                    />
                </DCol>
                <DCol sm="1">
                    <div class="form-group mb-0">
                        <label for="only-debt-loss">
                            {{ __('tools.invoices.only_debt_loss') }}
                        </label>
                        <div class="form-control border-0 pl-0">
                            <DSwitch
                                id="only-debt-loss"
                                v-model="filterParams.only_debt_loss"
                                color="success"
                                variant="3d"
                            />
                        </div>
                    </div>
                </DCol>
            </DRow>
            <DRow
                class="mb-3"
            >
                <DCol sm="2">
                    <DInput
                        id="contract-id"
                        v-model="filterParams.contract_id"
                        type="number"
                        :label="__('customers.invoices.contract_id')"
                        :placeholder="__('customers.invoices.contract_id')"
                        class="mb-0"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        :label="__('tools.invoices.filters.brand')"
                        :options="brandOptions"
                        :selection="true"
                        selection-type="text"
                        @update="updateSelectedBrands"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2 pt-3"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('tools.invoices.filters.filter')"
                        @click="changePage(1)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </DCol>
        <InvoiceTable
            :fields="getFields"
            :invoices="invoices.data"
            :with-customer="true"
            :sorter-value="sorterValue"
            @update:ordering="updateOrder"
        />
        <DPagination
            v-if="invoices.last_page > 1"
            class="pb-2"
            :active-page="invoices.current_page"
            :pages="invoices.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
    <ListViewModal
        v-if="showListViewModal"
        :show-modal="showListViewModal"
        :user-list-view="userListView"
        @close-modal="showListViewModal = false"
    />
</template>

<script>
import InvoiceTable from '~@/Components/Tools/Invoices/Index'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import _ from 'lodash'

export default {
    name: 'InvoiceIndex',
    components: {
        InvoiceTable,
        ListViewModal
    },
    props: {
        invoices: {
            type: Object,
            default: () => {
                return {}
            }
        },
        countries: {
            type: Array,
            default: () => {
                return []
            }
        },
        filters: {
            type: Object,
            default: () => {
                return {}
            }
        },
        types: {
            type: Array,
            default: () => { return [] }
        },
        statuses: {
            type: Array,
            default: () => { return [] }
        },
        divergentDiamantStatuses: {
            type: Array,
            default: () => {
                return []
            }
        },
        brands: {
            type: Array,
            default: () => {
                return []
            }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    data () {
        return {
            filterParams: {
                from_date: this.filters.from_date,
                to_date: this.filters.to_date,
                country_code: this.filters.country_code,
                order_by: this.filters.order_by,
                order_by_asc: this.filters.order_by_asc ? 1 : 0,
                divergent_diamant_status: this.filters.divergent_diamant_status ?? false,
                types: this.filters.types ?? [],
                statuses: this.filters.statuses ?? [],
                only_debt_loss: this.filters.only_debt_loss ?? false,
                contract_id: this.filters.contract_id ?? null,
                brand_ids: this.filters.brand_ids ?? []
            },
            showListViewModal: false
        }
    },
    computed: {
        typeOptions () {
            const types = _.cloneDeep(this.types)

            for (const type in types) {
                if (this.filters.types.includes(types[type].value)) {
                    types[type].selected = true
                }
            }

            return types
        },
        statusOptions () {
            const statuses = _.cloneDeep(this.statuses)

            for (const status in statuses) {
                if (this.filters.statuses.includes(statuses[status].value)) {
                    statuses[status].selected = true
                }
            }

            return statuses
        },
        brandOptions () {
            return this.brands.map(brand => ({
                value: brand.value,
                text: brand.label,
                selected: this.filters.brand_ids.includes(String(brand.value))
            }))
        },
        countryOptions () {
            const mappedCountries = this.countries
            mappedCountries.unshift({
                value: '',
                label: ''
            })
            return mappedCountries
        },
        sorterValue () {
            if (!this.filterParams.order_by) return { column: 'id', asc: false }

            return {
                column: this.filterParams.order_by,
                asc: !!this.filters.order_by_asc
            }
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: item.column !== 'actions' && item.column !== 'icon' ? this.__('settings.list_view.' + item.list + '.' + item.column) : '',
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            return availableList
        }
    },
    methods: {
        updateOrder (event) {
            this.filterParams.order_by = event.column
            this.filterParams.order_by_asc = event.asc ? 1 : 0

            this.changePage(this.invoices.current_page)
        },
        changePage (page) {
            const queryParameters = Object.fromEntries(Object.entries(this.filterParams).filter(([_, v]) => v))

            if (page !== 1) {
                queryParameters.page = page
            }

            if (queryParameters.only_debt_loss === true) {
                queryParameters.only_debt_loss = 1
            }

            this.$inertia.get(this.$route('tools.invoices.index'), queryParameters)
        },
        resetFilters () {
            this.$inertia.get(this.$route('tools.invoices.index'))
        },
        updateSelectedTypes (values) {
            this.filterParams.types = values
        },
        updateSelectedStatuses (values) {
            this.filterParams.statuses = values
        },
        updateSelectedBrands (values) {
            this.filterParams.brand_ids = values
        }
    }
}
</script>
